import { useModalAction, useModalState } from "@/contexts/modal/Context.tsx";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { useBookMockTestMutation } from "@/data/exercises.ts";
import { useMe } from "@/data/user.ts";
import CalendarIllustration from "@/components/ui/CalendarIllustration.tsx";
import routes from "@/config/routes.ts";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button";
import { useTranslation } from "react-i18next";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { LoginUserItem } from "@/types";

interface ConfirmModalState {
  date: Dayjs;
  hour: string;
  exercise: string;
  tutor: string;
}

export default function ConfirmMockTestModal() {
  const { data } = useModalState();
  const { closeModal } = useModalAction();
  const { t } = useTranslation();

  const { date, hour, exercise, tutor } = data as ConfirmModalState;

  const { me } = useMe();
  const user = me?.item.user;

  const navigate = useNavigate();

  const { mutate, isLoading } = useBookMockTestMutation(closeModal);

  const handleConfirm = () => {
    if (!user) return navigate(routes.login);

    mutate({
      user_id: user.id,
      tutor_token: tutor,
      user_token: user.token,
      data: date.format("YYYY-MM-DD"),
      ora: hour,
      ora_fine: dayjs(date.format("YYYY-MM-DD") + " " + hour)
        .add(30, "minutes")
        .format("HH:mm"),
      token_ex: exercise,
      type: "Mock test",
    });
  };

  // mixpanel integration
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise", me?.item?.user as LoginUserItem),
  );

  return (
    <div>
      <div className="flex items-stretch flex-col gap-4 justify-between h-full">
        <div className={"flex flex-col items-center w-full"}>
          <div className={"space-y-2"}>
            <p className={"text-brand text-2xl"}>
              {t("one_to_one.desktop_subtitle_section")}
            </p>
          </div>
        </div>
        <div className=" flex flex-row items-center justify-start gap-x-8 w-full my-4">
          <CalendarIllustration
            className="w-[206px] h-[215px] bg-brand-deep border-brand-deep border-solid border-2 rounded-lg"
            header={date.format("ddd")}
            day={date.format("DD")}
            month={date.format("MMM")}
          />
          <div className="flex flex-col gap-4">
            <div className={"space-y-0.5"}>
              <h5 className={"text-brand"}>{t("one_to_one.confirm_first_session_modal.time.title")}</h5>
              <p className={"text-4xl font-semibold text-brand-secondary"}>
                {hour}
              </p>
            </div>
            <div className={"space-y-0.5"}>
              <h5 className={"text-brand"}>{t("one_to_one.confirm_first_session_modal.duration.title")}</h5>
              <p className={"text-4xl font-semibold text-brand-secondary"}>
                30&nbsp;minutes
              </p>
            </div>
          </div>
        </div>
        <div className="mt-auto flex justify-center gap-x-4 w-full">
          <Button
            data-track-props={mixpanelPropsData}
            data-track-eventtype={`Excersise_Mock_Test_Confirmation`}
            size={"md"}
            variant={"outlined"}
            disabled={isLoading}
            onClick={() => {
              closeModal();
              navigate(routes.exercises);
            }}
          >
            {t("btns.exit")}
          </Button>
          <LoadingButton
            data-track-props={mixpanelPropsData}
            data-track-eventtype={`Excersise_Mock_Test_Confirmation`}
            size={"md"}
            color={"green"}
            type="submit"
            onClick={handleConfirm}
            disabled={isLoading}
            loading={isLoading}
          >
            {t("btns.confirm")}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
